import { moNum } from "@/lib/moNum";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertTriangle, Printer } from "lucide-react";
import { fmt, breakdown } from "./shelfShared";

export function PullListPanel({ activeMos, pullMoId, onSelectMo, pullPlan, pullLoading, pulled, onTogglePulled, onJump, onPrint }) {
  return (
    <div className="border border-slate-200" data-testid="pull-list-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-[#002FA7] px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-white">Production Pull List</span>
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-blue-100">Order</label>
          <Select value={pullMoId} onValueChange={onSelectMo}>
            <SelectTrigger className="h-8 w-72 rounded-sm bg-white text-xs" data-testid="pull-order-select">
              <SelectValue placeholder="Select an order to pull for…" />
            </SelectTrigger>
            <SelectContent>
              {activeMos.length === 0 ? (
                <div className="px-2 py-2 text-xs text-slate-400">No active orders</div>
              ) : activeMos.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {moNum(m.mwo_number)} · {m.product_name}{m.design_name ? ` · ${m.design_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {pullMoId && pullPlan && (
            <button onClick={onPrint} className="flex h-8 items-center gap-1.5 rounded-sm bg-white/15 px-2.5 text-xs font-semibold text-white transition-colors hover:bg-white/25" data-testid="pull-print-btn" title="Print pull ticket">
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
          )}
        </div>
      </div>
      <div className="p-3" data-testid="pull-list-body">
        {pullLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : !pullMoId ? (
          <p className="py-4 text-center text-xs text-slate-400">Pick an order above to see exactly where to pull each component from.</p>
        ) : !pullPlan || pullPlan.components.length === 0 ? (
          <p className="py-4 text-center text-xs text-slate-400">Nothing left to pull for this order.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-[11px] text-slate-500">Remaining to produce: <b className="text-slate-800">{fmt(pullPlan.remaining_bottles)}</b> bottles</p>
            {pullPlan.components.map((c) => (
              <div key={c.type_id + c.design_id} className="rounded-sm border border-slate-200" data-testid={`pull-comp-${c.type_id}${c.design_id ? `-${c.design_id}` : ""}`}>
                <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5">
                  <span className="text-sm font-semibold text-slate-800">{c.name}{c.type ? ` · ${c.type}` : ""}</span>
                  <div className="flex items-center gap-2">
                    {c.locations.length > 0 && (
                      <span className="text-[10px] font-semibold text-emerald-600" data-testid={`pull-progress-${c.type_id}`}>
                        {c.locations.filter((l) => pulled[l.location]).length}/{c.locations.length} pulled
                      </span>
                    )}
                    <span className="text-xs text-slate-500">Need {breakdown(c.required, c.units_per_case, c.units_per_pallet).join(" · ")}</span>
                  </div>
                </div>
                <div className="divide-y divide-slate-50">
                  {c.locations.map((l, i) => {
                    const done = !!pulled[l.location];
                    return (
                    <div key={l.location} className={`flex items-center justify-between gap-2 px-3 py-1.5 text-sm transition-colors ${done ? "bg-emerald-50/60" : ""}`} data-testid={`pull-loc-${c.type_id}-${i}`}>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input type="checkbox" checked={done} onChange={() => onTogglePulled(l.location)} className="h-3.5 w-3.5 accent-emerald-600" data-testid={`pull-check-${l.location}`} />
                        <button type="button" onClick={() => onJump(l.location)} className={`font-mono font-bold text-[#002FA7] hover:underline ${done ? "line-through opacity-50" : ""}`} data-testid={`pull-jump-${l.location}`}>{l.location}</button>
                      </label>
                      <span className={`text-slate-700 ${done ? "line-through opacity-50" : ""}`}>{breakdown(l.units, l.units_per_case, l.units_per_pallet).join(" · ")}</span>
                    </div>
                    );
                  })}
                  {c.locations.length === 0 && (
                    <div className="px-3 py-1.5 text-xs text-slate-400">Not placed on any shelf.</div>
                  )}
                  {c.unassigned_needed > 0 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700" data-testid={`pull-unassigned-${c.type_id}`}>
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> + {breakdown(c.unassigned_needed, c.units_per_case, c.units_per_pallet).join(" · ")} not on a shelf (Unassigned)
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
